import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
})

type State =
  | { kind: 'loading' }
  | { kind: 'valid' }
  | { kind: 'already' }
  | { kind: 'invalid' }
  | { kind: 'submitting' }
  | { kind: 'done' }
  | { kind: 'error'; message: string }

function UnsubscribePage() {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('token')
    setToken(t)
    if (!t) {
      setState({ kind: 'invalid' })
      return
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then(async (r) => {
        const data = await r.json()
        if (!r.ok) return setState({ kind: 'invalid' })
        if (data.valid === false && data.reason === 'already_unsubscribed') {
          setState({ kind: 'already' })
        } else if (data.valid) {
          setState({ kind: 'valid' })
        } else {
          setState({ kind: 'invalid' })
        }
      })
      .catch(() => setState({ kind: 'invalid' }))
  }, [])

  const confirm = async () => {
    if (!token) return
    setState({ kind: 'submitting' })
    try {
      const r = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await r.json()
      if (data.success) setState({ kind: 'done' })
      else if (data.reason === 'already_unsubscribed') setState({ kind: 'already' })
      else setState({ kind: 'error', message: data.error || 'Something went wrong' })
    } catch (e: any) {
      setState({ kind: 'error', message: e?.message || 'Network error' })
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-5 py-16">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-[var(--shadow-lift)]">
        <h1 className="font-display text-2xl text-foreground">Email preferences</h1>
        <div className="mt-4 text-sm text-foreground/75">
          {state.kind === 'loading' && <p>Checking your link…</p>}
          {state.kind === 'invalid' && (
            <p>This unsubscribe link is invalid or has expired.</p>
          )}
          {state.kind === 'already' && (
            <p>You're already unsubscribed. You won't receive further emails.</p>
          )}
          {state.kind === 'valid' && (
            <>
              <p>Click below to confirm and stop receiving emails from Jerry's Therapy.</p>
              <button
                onClick={confirm}
                className="mt-6 inline-flex items-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:-translate-y-0.5 transition-transform"
              >
                Confirm unsubscribe
              </button>
            </>
          )}
          {state.kind === 'submitting' && <p>Updating your preferences…</p>}
          {state.kind === 'done' && (
            <p>You've been unsubscribed. Sorry to see you go.</p>
          )}
          {state.kind === 'error' && <p className="text-destructive">{state.message}</p>}
        </div>
      </div>
    </main>
  )
}
